import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiCheck, FiUsers, FiTrendingUp, FiStar, FiPlay, FiInfo, FiChevronDown, FiChevronUp } = FiIcons;

function CohortLandingPage() {
  const [stickyVisible, setStickyVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('getting-to-know-you');
  const [transcriptVisible, setTranscriptVisible] = useState(false);
  const currentYear = new Date().getFullYear();
  const checkoutUrl = "https://courses.itskaivalya.com/checkouts/checkout-page/?add-to-cart=67&quantity=1&aero-coupons=cohort-416";

  useEffect(() => {
    const handleScroll = () => {
      setStickyVisible(window.scrollY > 800);
      
      // Update active section based on scroll position
      const sections = [
        'getting-to-know-you', 'basics', 'app-concept', 'validating-the-concept',
        'wireframing', 'design-briefs', 'development', 'marketing-content',
        'automation', 'final-thoughts', 'bonus'
      ];
      
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 200 && rect.bottom >= 200) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCTAClick = () => {
    window.open(checkoutUrl, '_blank');
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      element.focus();
    }
  };

  const curriculumSections = [
    { id: 'getting-to-know-you', title: 'Getting to know you', description: 'Introduction and orientation.' },
    { id: 'basics', title: 'Basics', description: 'Core foundations of AI-assisted full-stack development.' },
    { id: 'app-concept', title: 'App Concept', description: 'Understanding and scoping app ideas.' },
    { id: 'validating-the-concept', title: 'Validating the Concept', description: 'Using AI and research methods to validate feasibility and demand.' },
    { id: 'wireframing', title: 'Wireframing', description: 'Designing the flow and structure before development.' },
    { id: 'design-briefs', title: 'Design Briefs', description: 'Translating wireframes into actionable design specs.' },
    { id: 'development', title: 'Development', description: 'Hands-on building with AI support for frontend, backend, auth, and APIs.' },
    { id: 'marketing-content', title: 'Marketing Content', description: 'Creating the launch assets and positioning around your app.' },
    { id: 'automation', title: 'Automation', description: 'Using AI tools to streamline workflows and app processes.' },
    { id: 'final-thoughts', title: 'Final Thoughts', description: 'Wrap-up, polishing, and preparing your portfolio.' },
    { id: 'bonus', title: 'Bonus', description: 'Extra tips, case studies, and advanced techniques.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Skip to content link */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-black text-white px-4 py-2 rounded-lg z-50 focus:outline-none focus:ring-4 focus:ring-gray-300"
      >
        Skip to main content
      </a>

      {/* Hero Section */}
      <section className="bg-white py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-6xl font-bold text-black mb-8 leading-tight"
          >
            Join the Next Cohort: Build Real AI-Powered Apps — Together
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl md:text-2xl text-gray-600 mb-8"
          >
            Forget trying to learn alone. Forget half-finished tutorials that break when you touch them.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-lg md:text-xl text-gray-700 mb-8 space-y-4"
          >
            <p>Here, you'll build production-ready apps live in sprints — alongside peers, guided step by step, with weekly accountability calls pushing you forward.</p>
            
            <p className="font-semibold">We don't "talk theory." We ship projects:</p>
            
            <div className="space-y-2 text-left max-w-3xl mx-auto">
              <p><strong>Annapurna Foods</strong> — a Zomato-style food ordering platform with Razorpay payments, carts, and admin dashboards.</p>
              <p><strong>Kushl.app</strong> — a student–employer marketplace with authentication, tasks, certifications, and payments.</p>
              <p><strong>Yatric platform</strong> — a host–guest marketplace with maps, filters, approvals, and role-based profiles.</p>
              <p><strong>Solar ROI Calculator</strong> — interactive tool wired to a backend, used in real-world pitches.</p>
              <p><strong>Restaurant Lead-Gen Pages</strong> — WhatsApp coupon funnels for recurring customers.</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-8"
          >
            <p className="text-3xl font-bold text-black mb-2">Monthly tuition: ₹416/mo</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <button
              onClick={handleCTAClick}
              className="inline-flex items-center gap-3 bg-black hover:bg-gray-800 focus:bg-gray-800 text-white font-bold px-8 py-4 rounded-lg text-xl transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-gray-300 min-h-[44px]"
            >
              Reserve your seat — get community access now
              <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
            </button>
          </motion.div>
        </div>
      </section>

      <main id="main-content">
        {/* Primary Curriculum Section */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-black mb-4">
                What you'll learn inside (Modules Overview)
              </h1>
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="bg-black text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
                  KD
                </div>
                <p className="text-lg text-gray-600">By Kaivalya Deshpande</p>
              </div>
              <p className="text-gray-600">Categories: ai</p>
            </div>

            <div className="grid lg:grid-cols-4 gap-8">
              {/* Curriculum Navigation */}
              <div className="lg:col-span-1">
                <nav aria-label="Course curriculum" className="sticky top-6">
                  <h3 className="text-lg font-bold text-black mb-4">Course Curriculum</h3>
                  <ol className="space-y-2">
                    {curriculumSections.map((section, index) => (
                      <li key={section.id}>
                        <button
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full text-left px-4 py-3 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                            activeSection === section.id
                              ? 'bg-black text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                          }`}
                          aria-current={activeSection === section.id ? 'true' : 'false'}
                          aria-controls={section.id}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                              {index + 1}. {section.title}
                            </span>
                            {section.hasInfo && (
                              <SafeIcon icon={FiInfo} className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ol>
                </nav>
              </div>

              {/* Course Content */}
              <div className="lg:col-span-3">
                {/* YouTube Course Trailer */}
                <div 
                  className="mb-8" 
                  role="region" 
                  aria-label="Course preview video"
                >
                  <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
                    <iframe 
                      src="https://www.youtube.com/embed/JZXaqpxG9ow?rel=0&modestbranding=1" 
                      title="Course preview and trailer" 
                      width="100%" 
                      height="480" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                      className="w-full h-full"
                    ></iframe>
                  </div>
                  
                  {/* Off-screen description for screen readers */}
                  <p id="trailer-desc" className="sr-only">
                    Course preview trailer showing the AI development program structure, sample projects, and learning outcomes for building full-stack applications.
                  </p>
                  
                  {/* Transcript/Summary */}
                  <div className="mt-4">
                    <button
                      onClick={() => setTranscriptVisible(!transcriptVisible)}
                      className="flex items-center gap-2 text-gray-700 hover:text-black font-medium focus:outline-none focus:ring-2 focus:ring-gray-400 rounded px-2 py-1"
                      aria-expanded={transcriptVisible}
                      aria-controls="video-transcript"
                    >
                      <SafeIcon icon={transcriptVisible ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
                      {transcriptVisible ? 'Hide' : 'Show'} Transcript
                    </button>
                    
                    {transcriptVisible && (
                      <div 
                        id="video-transcript" 
                        className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <h4 className="font-semibold text-black mb-2">Video Transcript:</h4>
                        <p className="text-gray-700 leading-relaxed">
                          Course preview: A short trailer showing what you will build in this program — production-ready apps, live sprints, and the outcomes you can expect. Watch to see sample projects, cohort-style demos, and the practical, results-first approach we use.
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Fallback for when YouTube is blocked */}
                  <noscript>
                    <div className="bg-gray-100 rounded-lg p-8 text-center">
                      <p className="text-gray-700 mb-4">Video preview not available. Watch the course trailer:</p>
                      <a 
                        href="https://www.youtube.com/watch?v=JZXaqpxG9ow" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        <SafeIcon icon={FiPlay} className="w-5 h-5" />
                        Watch on YouTube
                      </a>
                    </div>
                  </noscript>
                </div>

                {/* Curriculum Sections Content */}
                <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
                  <div className="prose prose-gray max-w-none">
                    {curriculumSections.map((section) => (
                      <div 
                        key={section.id} 
                        id={section.id} 
                        className="mb-12 scroll-mt-6" 
                        tabIndex="-1"
                        role="region"
                        aria-labelledby={`${section.id}-heading`}
                      >
                        <h3 
                          id={`${section.id}-heading`}
                          className="text-xl font-bold text-black mb-4 border-b border-gray-200 pb-2"
                        >
                          Module {curriculumSections.findIndex(s => s.id === section.id) + 1}: {section.title}
                        </h3>
                        <div className="text-gray-700">
                          <p className="mb-4">{section.description}</p>
                          <p>This program includes weekly live sessions with Kaivalya for debugging and Q&A, plus recorded sessions for self-paced catch-up.</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why most people get stuck */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Why most people get stuck</h2>
            
            <div className="mb-8">
              <p className="text-xl text-gray-700 mb-6">If you've tried to learn AI app building solo, you know the frustration:</p>
              <ul className="space-y-4 text-lg text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-gray-600 mt-1">•</span>
                  Tutorials work only in polished demos, not on your laptop.
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gray-600 mt-1">•</span>
                  AI outputs "hallucinated" junk when things get complex.
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gray-600 mt-1">•</span>
                  You're stuck debugging errors, with nobody to ask.
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gray-600 mt-1">•</span>
                  Progress slows, motivation drops, and projects die half-built.
                </li>
              </ul>
            </div>

            <div className="mb-8 space-y-4 text-lg text-gray-700">
              <p>And here's the truth: building in isolation kills momentum.</p>
              <p className="font-semibold">That's why cohort-based learning works: shared accountability, community energy, live feedback.</p>
            </div>
          </div>
        </section>

        {/* What if you had a clear path */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">What if you had a clear path</h2>
            
            <div className="space-y-6 text-lg text-gray-700">
              <p>What if…</p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                  In 2 weeks, you were demoing your first login + database flow to your cohort?
                </li>
                <li className="flex items-start gap-3">
                  <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                  In 4 weeks, your ordering app (like Annapurna Foods) was live with Razorpay payments?
                </li>
                <li className="flex items-start gap-3">
                  <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                  In 6 weeks, your Yatric marketplace was running with maps, filters, profiles, and approvals?
                </li>
                <li className="flex items-start gap-3">
                  <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                  In 8 weeks, you were presenting dashboards + AI workflows to peers — showing skills that land jobs, clients, and promotions?
                </li>
              </ul>
              <p className="text-xl font-semibold text-center mt-8">
                Imagine the buzz of demo day: you present, peers cheer, and you walk away with real proof.
              </p>
            </div>
          </div>
        </section>

        {/* Real apps we've already built */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Real apps we've already built</h2>
            
            <div className="space-y-6 text-lg text-gray-700">
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">Annapurna Foods</h3>
                <p>A Zomato-style food ordering platform with Razorpay payments, carts, and admin dashboards.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">Kushl.app</h3>
                <p>A student–employer marketplace with authentication, tasks, certifications, and payments.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">Yatric platform</h3>
                <p>A host–guest marketplace with maps, filters, approvals, and role-based profiles.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">Solar ROI Calculator</h3>
                <p>Interactive tool wired to a backend, used in real-world pitches.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">Restaurant Lead-Gen Pages</h3>
                <p>WhatsApp coupon funnels for recurring customers.</p>
              </div>
            </div>

            <div className="text-center mt-12">
              <p className="text-xl font-semibold text-black mb-2">Every project was built in days, not months. And now you'll build them too.</p>
            </div>
          </div>
        </section>

        {/* Who this program is designed for */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Who this program is designed for</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <SafeIcon icon={FiUsers} className="w-12 h-12 text-gray-800 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-black mb-4">Students</h3>
                <p className="text-gray-700">Needing undeniable portfolio projects with live links.</p>
              </div>
              <div className="text-center">
                <SafeIcon icon={FiTrendingUp} className="w-12 h-12 text-gray-800 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-black mb-4">Interns/Juniors</h3>
                <p className="text-gray-700">Aiming for promotions by shipping visible features.</p>
              </div>
              <div className="text-center">
                <SafeIcon icon={FiStar} className="w-12 h-12 text-gray-800 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-black mb-4">Freelancers</h3>
                <p className="text-gray-700">Needing speed, clarity, and higher-paying clients.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Common questions and concerns */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Common questions and concerns</h2>
            
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">"I can't code."</h3>
                <p className="text-gray-700">→ Perfect. We scaffold with AI. Only minimal code where it helps.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">"AI lies."</h3>
                <p className="text-gray-700">→ True. That's why we validate, fix live, and teach stacks that actually work.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">"What if I get stuck?"</h3>
                <p className="text-gray-700">→ Weekly calls + peer help = you'll never be alone.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">"What if I waste money?"</h3>
                <p className="text-gray-700">→ 60-day money-back guarantee.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Proof from our students */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Proof from our students</h2>
            
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-3xl font-bold text-black mb-2">537+</p>
                <p className="text-lg text-gray-700">learners already onboard.</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-black mb-2">41–59%</p>
                <p className="text-lg text-gray-700">salary bumps reported.</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-black mb-2">3–7 weeks</p>
                <p className="text-lg text-gray-700">Freelancers landing clients.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Frequently asked questions */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Frequently asked questions</h2>
            
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">Recorded or live?</h3>
                <p className="text-gray-700">→ Both. Weekly live calls + recordings.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">Need coding?</h3>
                <p className="text-gray-700">→ No prior coding required.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">Laptop type?</h3>
                <p className="text-gray-700">→ Any modern laptop.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">Support?</h3>
                <p className="text-gray-700">→ Active community + Q&As.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">Refund?</h3>
                <p className="text-gray-700">→ 60-day full guarantee.</p>
              </div>
            </div>
          </div>
        </section>

        {/* What makes this different */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12">What makes this different</h2>
            
            <div className="space-y-6 text-xl text-gray-700">
              <p>This isn't slides. This isn't fluff.</p>
              <p>We build live, on screen — mistakes included, fixes included, real deployments included.</p>
              <p className="font-bold text-black">You leave with apps that work, skills that stick, and proof that gets noticed.</p>
            </div>

            <div className="mt-12">
              <button
                onClick={handleCTAClick}
                className="inline-flex items-center gap-3 bg-black hover:bg-gray-800 focus:bg-gray-800 text-white font-bold px-8 py-4 rounded-lg text-xl transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-gray-300 min-h-[44px]"
              >
                Reserve your seat — get community access now
                <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
              </button>
            </div>
          </div>
        </section>

        {/* Instructor Section */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Meet Your Instructor: Kaivalya</h2>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://i.ibb.co/DHSRjPSv/kaivalya-profile-photo.jpg" 
                  alt="Instructor Kaivalya Deshpande"
                  className="w-full max-w-md mx-auto rounded-lg shadow-lg"
                />
              </div>
              <div className="space-y-6">
                <p className="text-lg text-gray-700">
                  I've built and shipped dozens of AI-powered applications, from marketplaces handling thousands of users to business tools generating real revenue.
                </p>
                <p className="text-lg text-gray-700">
                  But more importantly, I've been where you are: stuck in tutorial hell, copy-pasting code that doesn't work, wondering if I'd ever build anything real.
                </p>
                <p className="text-lg text-gray-700">
                  That's why I created this program. Not to show off what I know, but to give you the exact system I wish I had when I started.
                </p>
                <p className="text-lg font-semibold text-black">
                  My promise: I'll be there every week, debugging your code, answering your questions, and making sure you actually ship real projects.
                </p>
                
                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-black">53+</p>
                    <p className="text-sm text-gray-600">Apps Built & Deployed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-black">537+</p>
                    <p className="text-sm text-gray-600">Students Guided</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-black">3.5</p>
                    <p className="text-sm text-gray-600">Years Teaching AI Development</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Meet the Maitreya Labs Team</h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <img 
                src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/maitreya%20labs%20team%2001.jpg-CYzk0EHjNYGm0lsvWJXs0UcNAluuHS.jpeg" 
                alt="Maitreya Labs Team Member 1"
                className="w-full rounded-lg shadow-lg"
              />
              <img 
                src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/maitreya%20labs%20charty%20and%20ngo%20sessions.jpg-gOY6rfvY4MYCc5Wwcxq8Q4sS30vBBv.jpeg" 
                alt="Maitreya Labs Charity and NGO Sessions"
                className="w-full rounded-lg shadow-lg"
              />
              <img 
                src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Team%20Maitreya%20Labs%20team%202%20%281%29.png-nLZY55sOGLxWPnzzzhLt4Xo4NzhgH0.jpeg" 
                alt="Maitreya Labs Team Member 2"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
            
            <p className="text-lg text-gray-700 text-center">
              From technical workshops to community outreach, we're committed to building not just better developers, but a better tech community that uses AI for positive impact.
            </p>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Real Results: What Clients Say</h2>
            
            <div className="max-w-3xl mx-auto mb-8">
              <div className="wistia_responsive_padding" style={{padding:'177.78% 0 0 0', position:'relative'}}>
                <div className="wistia_responsive_wrapper" style={{height:'100%', left:0, position:'absolute', top:0, width:'100%'}}>
                  <iframe 
                    src="https://fast.wistia.net/embed/iframe/pjhz34kw16?web_component=true&seo=true"
                    title="Nihal Testimonial Video"
                    allow="autoplay; fullscreen"
                    allowTransparency="true"
                    frameBorder="0"
                    scrolling="no"
                    className="wistia_embed"
                    name="wistia_embed"
                    width="100%"
                    height="100%">
                  </iframe>
                </div>
              </div>
            </div>
            
            <div className="space-y-6 text-lg text-gray-700">
              <p>
                Using the same AI development techniques taught in this course, we've built complete applications, marketing funnels, and business solutions for clients across various industries.
              </p>
              <p>
                The result? Exceptional client satisfaction and testimonials like this one from Nihal.
              </p>
              <p className="font-semibold">
                Behind the scenes: The application and marketing systems that earned Nihal's praise were built entirely using AI-assisted development—the same methods you'll master in this course.
              </p>
            </div>
          </div>
        </section>

        {/* Student Success Stories */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Student Success Stories</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg text-center border border-gray-200 shadow-sm">
                <p className="text-2xl font-bold text-black mb-2">"₹45,000 in first month"</p>
                <p className="text-gray-700">→ Landed 3 restaurant clients.</p>
              </div>
              <div className="bg-white p-6 rounded-lg text-center border border-gray-200 shadow-sm">
                <p className="text-2xl font-bold text-black mb-2">"Recurring client pays ₹20,000/month"</p>
                <p className="text-gray-700">→ Built an e-commerce store with ongoing revenue.</p>
              </div>
              <div className="bg-white p-6 rounded-lg text-center border border-gray-200 shadow-sm">
                <p className="text-2xl font-bold text-black mb-2">"Quit my job after 3 months"</p>
                <p className="text-gray-700">→ Now earning 2x corporate salary as a freelance AI developer.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-400">© {currentYear} ItsKaivalya. All rights reserved.</p>
        </div>
      </footer>

      {/* Sticky CTA Bar */}
      {stickyVisible && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-40">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              <p className="font-bold text-black">Monthly tuition: ₹416/mo</p>
            </div>
            <button
              onClick={handleCTAClick}
              className="inline-flex items-center gap-2 bg-black hover:bg-gray-800 focus:bg-gray-800 text-white font-bold px-6 py-3 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-gray-300 min-h-[44px]"
            >
              Reserve your seat
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Wistia Script */}
      <script src="https://fast.wistia.net/player.js" async></script>
    </div>
  );
}

export default CohortLandingPage;